// Per-device preferences (not synced): who is usually holding this device,
// whether it's the wall kiosk, and how to reach the sync server.
//
// localStorage can throw or be wiped in kiosk browsers, so every access is
// guarded and the important bits (sync key) are mirrored into IndexedDB meta
// by the sync engine.

export interface DevicePrefs {
  deviceId: string;
  /** Person currently logging on this device. */
  personId?: string;
  kiosk: boolean;
  /** '' = same origin as the app. */
  serverUrl: string;
  key?: string;
  theme: 'auto' | 'light' | 'dark';
  chime: boolean;
  /** Return to Today after this many seconds idle (kiosk). 0 = never. */
  idleReturnSec: number;
  /** Show the dimmed night clock after this many seconds idle at night (kiosk). */
  nightDimSec: number;
  /** Daytime photo screensaver after this many seconds idle (kiosk). 0 = never. */
  ambientSec: number;
  /** Read Ask answers aloud. */
  speak: boolean;
  /** Neural voice id (when the server has a speech key). */
  voice?: string;
  /** Speaking speed, percent faster (+) or slower (−). */
  voiceRate: number;
  /** After an answer, listen again for a follow-up. */
  handsFree: boolean;
  /** Set when this device joined with a sitter link. */
  guest?: { name: string; expires: number };
  /** Set when this device signed in with Google (the key is then a session token). */
  account?: { email: string; name?: string };
}

/** The device's key is a Google sign-in session, not the household key. */
export const isSession = (key?: string) => !!key?.startsWith('gs1.');

const KEY = 'arlo.device';

function randomId(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return 'dev-' + Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

const defaults = (): DevicePrefs => ({
  deviceId: randomId(),
  kiosk: false,
  serverUrl: '',
  theme: 'auto',
  chime: true,
  idleReturnSec: 120,
  nightDimSec: 60,
  ambientSec: 300,
  speak: true,
  voiceRate: 0,
  handsFree: true,
});

let current: DevicePrefs = load();
const listeners = new Set<() => void>();

function load(): DevicePrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...defaults(), ...JSON.parse(raw) };
  } catch {
    /* storage unavailable */
  }
  const fresh = defaults();
  save(fresh);
  return fresh;
}

function save(p: DevicePrefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable */
  }
}

export function devicePrefs(): DevicePrefs {
  return current;
}

export function setDevicePrefs(patch: Partial<DevicePrefs>): void {
  current = { ...current, ...patch };
  save(current);
  for (const l of listeners) l();
}

export function subscribeDevice(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/**
 * Read bootstrap parameters from the launch URL so an MDM can configure the
 * kiosk in one go, e.g.  https://arlo.example.com/?kiosk=1&key=abcde-...
 * Parameters are removed from the address bar afterwards.
 */
export function applyLaunchParams(): void {
  let url: URL;
  try {
    url = new URL(location.href);
  } catch {
    return;
  }
  const patch: Partial<DevicePrefs> = {};
  const key = url.searchParams.get('key')?.trim();
  // Opening a sitter link on a household device mustn't downgrade it to a sitter.
  const ownerHere = !!current.key && !current.key.startsWith('sit-');
  if (key && !(key.startsWith('sit-') && ownerHere)) patch.key = key;
  const kiosk = url.searchParams.get('kiosk');
  if (kiosk !== null) patch.kiosk = kiosk !== '0' && kiosk !== 'false';
  const server = url.searchParams.get('server');
  if (server !== null) patch.serverUrl = server.replace(/\/+$/, '');
  const person = url.searchParams.get('person');
  if (person) patch.personId = person;
  if (Object.keys(patch).length) {
    setDevicePrefs(patch);
    for (const p of ['key', 'kiosk', 'server', 'person']) url.searchParams.delete(p);
    try {
      history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
    } catch {
      /* ignore */
    }
  }
}
